import { buttonVariants } from '@talentbabu/ui';
import { Compass, RotateCw, ShieldAlert, TriangleAlert } from 'lucide-react';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router';
import { homePathFor, useAuthStore } from '../../app/auth-store';

function ErrorShell({
  icon,
  code,
  title,
  message,
  children,
}: {
  icon: React.ReactNode;
  code: string;
  title: string;
  message: string;
  children?: React.ReactNode;
}) {
  const user = useAuthStore((s) => s.user);
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-4 py-16 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
        {icon}
      </div>
      <p className="text-sm font-semibold text-brand-600">{code}</p>
      <h1 className="mt-1 text-2xl font-bold text-slate-900">{title}</h1>
      <p className="mt-2 text-slate-500">{message}</p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        {children}
        <Link to="/" className={buttonVariants({ variant: 'outline' })}>
          Home
        </Link>
        {user && (
          <Link to={homePathFor(user.role)} className={buttonVariants()}>
            My dashboard
          </Link>
        )}
      </div>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <ErrorShell
      icon={<Compass className="h-7 w-7" />}
      code="404"
      title="Page not found"
      message="The page you are looking for doesn’t exist or has moved."
    />
  );
}

export function ForbiddenPage() {
  return (
    <ErrorShell
      icon={<ShieldAlert className="h-7 w-7" />}
      code="403"
      title="Access denied"
      message="Your account doesn’t have permission to view this page. Contact your administrator if you think this is a mistake."
    />
  );
}

/** Route-level error boundary: friendly page instead of React Router's developer screen. */
export function RouteErrorPage() {
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;
  if (isRouteErrorResponse(error) && error.status === 403) return <ForbiddenPage />;

  const message = error instanceof Error ? error.message : String(error);
  // A page bundle from a previous deploy is gone; a reload fetches the new one.
  const staleBundle = /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(message);

  return (
    <ErrorShell
      icon={<TriangleAlert className="h-7 w-7" />}
      code={staleBundle ? 'Update available' : 'Error'}
      title={staleBundle ? 'A new version is available' : 'Something went wrong'}
      message={
        staleBundle
          ? 'TalentBabu was updated while this tab was open. Reload to continue.'
          : 'This page hit an unexpected problem. Reloading usually fixes it. If it keeps happening, please contact support.'
      }
    >
      <button type="button" onClick={() => window.location.reload()} className={buttonVariants()}>
        <RotateCw className="h-4 w-4" /> Reload page
      </button>
      {import.meta.env.DEV && (
        <pre className="mt-4 w-full overflow-x-auto rounded-lg bg-slate-100 p-3 text-left text-xs text-slate-600">
          {message}
        </pre>
      )}
    </ErrorShell>
  );
}
